/**
 * WebSocket 传输：网络链路。
 *
 * 适用场景：机载伴飞计算机、4G/5G 数传模块的网络出口、`mavlink2rest` 风格的网关、
 * Herelink 这类自带网络转发的设备。
 *
 * 不做自动重连：重连策略（退避、上限、是否要用户确认）属于链路管理，
 * 由上层统一决定。这里只负责把**为什么断了**说清楚。
 */
import { BaseTransport, type TransportKind, type TransportMetrics } from './transport';

export interface WebSocketTransportOptions {
  readonly url: string;
  readonly protocols?: readonly string[];
  /** 连接超时，默认 10 秒。浏览器自身的握手超时可能长达一分钟，等不了。 */
  readonly connectTimeoutMs?: number;
  /** 注入 WebSocket 实现，供测试使用 */
  readonly factory?: (url: string, protocols: readonly string[] | undefined) => WebSocket;
  /** 本机时钟，供测试注入 */
  readonly now?: () => number;
}

/**
 * HTTPS 页面连不上 `ws://`（混合内容），`localhost` / `127.0.0.1` 例外。
 *
 * 这条检查必须放在应用里：浏览器的报错是"连接被拒绝"之类看不出原因的信息，
 * 而真实原因是"这个页面是 HTTPS 的"。排障时这是最浪费时间的一类问题。
 */
function mixedContentProblem(url: string): string | null {
  if (typeof window === 'undefined') return null;
  if (window.location.protocol !== 'https:') return null;
  if (!url.startsWith('ws://')) return null;

  const host = url.slice('ws://'.length).split(/[:/]/)[0] ?? '';
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') return null;

  return `当前页面是 HTTPS，浏览器不允许连接明文 ${url}。请改用 wss://，或把该地址换成 localhost。`;
}

export class WebSocketTransport extends BaseTransport {
  readonly kind: TransportKind = 'websocket';

  private socket: WebSocket | null = null;
  private readonly url: string;
  private readonly protocols: readonly string[] | undefined;
  private readonly connectTimeoutMs: number;
  private readonly factory: (url: string, protocols: readonly string[] | undefined) => WebSocket;
  private readonly now: () => number;
  private pingSentAt: number | null = null;
  private latencyMs: number | null = null;

  constructor(options: WebSocketTransportOptions) {
    super();
    this.url = options.url;
    this.protocols = options.protocols;
    this.connectTimeoutMs = options.connectTimeoutMs ?? 10_000;
    this.now = options.now ?? (() => Date.now());
    this.factory =
      options.factory ??
      ((url, protocols) =>
        protocols === undefined ? new WebSocket(url) : new WebSocket(url, [...protocols]));
  }

  get label(): string {
    return this.url;
  }

  /** `linkMetrics` 是 `Transport` 的可选成员，基类没有实现，因此不加 `override` */
  linkMetrics(): TransportMetrics {
    return { latencyMs: this.latencyMs, rssi: null };
  }

  override open(): Promise<void> {
    const problem = mixedContentProblem(this.url);
    if (problem !== null) {
      this.setState('error', problem);
      return Promise.reject(new Error(problem));
    }

    this.setState('opening');

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let socket: WebSocket;

      try {
        socket = this.factory(this.url, this.protocols);
      } catch (error) {
        const detail = error instanceof Error ? error.message : String(error);
        this.setState('error', `无法创建 WebSocket：${detail}`);
        reject(error instanceof Error ? error : new Error(detail));
        return;
      }

      this.socket = socket;
      socket.binaryType = 'arraybuffer';

      const timeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        this.setState('error', `${this.connectTimeoutMs}ms 内没有连上 ${this.url}`);
        try {
          socket.close();
        } catch {
          /* 关不掉也无所谓，下面已经报错了 */
        }
        reject(new Error('连接超时'));
      }, this.connectTimeoutMs);

      socket.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        this.pingSentAt = this.now();
        this.setState('open');
        resolve();
      };

      socket.onmessage = (event) => {
        const data = event.data;
        if (data instanceof ArrayBuffer) {
          this.emitData(new Uint8Array(data));
          return;
        }
        // 对端用文本帧发 MAVLink 是常见的配置错误（应当用二进制帧）。
        // 报出来比静默丢弃有用得多。
        if (typeof data === 'string') {
          this.setState('error', '收到了文本帧，但 MAVLink 必须是二进制帧。请检查对端的发送方式。');
        }
      };

      socket.onerror = () => {
        // 浏览器的 error 事件不带任何原因，只能说"连不上"
        if (settled) {
          this.setState('error', `${this.url} 连接中断`);
          return;
        }
        settled = true;
        clearTimeout(timeout);
        this.setState('error', `无法连接 ${this.url}（地址、端口或对端未启动）`);
        reject(new Error('WebSocket 连接失败'));
      };

      socket.onclose = (event) => {
        clearTimeout(timeout);
        const reason = event.reason === '' ? '' : `：${event.reason}`;
        if (this.currentState === 'open') {
          this.setState('error', `${this.url} 连接已关闭（code ${event.code}）${reason}`);
          this.socket = null;
          return;
        }
        if (!settled) {
          settled = true;
          this.setState('error', `连接被关闭（code ${event.code}）${reason}`);
          reject(new Error('WebSocket 连接被关闭'));
        }
      };
    });
  }

  override async close(): Promise<void> {
    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        /* 已经断开的 socket 再关会抛错，忽略 */
      }
    }
    this.setState('closed');
  }

  override write(bytes: Uint8Array): Promise<void> {
    const socket = this.socket;
    if (socket === null || socket.readyState !== 1) {
      return Promise.reject(new Error('链路未打开，无法发送'));
    }
    // 顺便用"发出去到对端有响应"来估算时延（粗略但比没有强）
    if (this.pingSentAt !== null) {
      this.latencyMs = this.now() - this.pingSentAt;
      this.pingSentAt = null;
    }
    socket.send(bytes);
    return Promise.resolve();
  }
}
